import axios from "./axios";
import { handleApiError } from "./handleApiError";

export type FinanceLedgerKind = "all" | "sales" | "token";

export interface FinanceLedgerItem {
  id: string;
  sourceType: string;
  occurredAtUtc: string;
  userId?: string | null;
  username?: string | null;
  contentId?: string | null;
  contentPartId?: string | null;
  creatorId?: string | null;
  tokenAmount?: number;
  currencyAmount?: number;
  currencyCode?: string;
  status?: string;
  referenceId?: string | null;
  description?: string | null;
}

export interface FinanceLedgerResponse {
  totalCount: number;
  items: FinanceLedgerItem[];
}

export interface FinanceReconciliationIssue {
  id: string;
  issueType: string;
  severity: "high" | "medium" | "low";
  status: string;
  occurredAtUtc: string;
  referenceId?: string | null;
  detail?: string | null;
}

export interface FinanceReconciliationResponse {
  totalIssues: number;
  highSeverity: number;
  mediumSeverity: number;
  lowSeverity: number;
  items: FinanceReconciliationIssue[];
}

export interface FinancePayoutSourceBreakdown {
  sourceType: string;
  entryCount: number;
  grossTokenAmount: number;
  shareTokenAmount: number;
  currencyAmount: number;
}

export interface FinancePayoutStatementEntry {
  id: string;
  sourceType: string;
  contentId?: string | null;
  contentPartId?: string | null;
  purchaseId?: string | null;
  tokenAmount: number;
  shareTokenAmount: number;
  sharePercent: number;
  currencyAmount: number;
  currencyCode?: string | null;
  occurredAtUtc: string;
  metadataJson?: string | null;
}

export interface FinancePayoutStatement {
  payoutId: string;
  creatorId: string;
  creatorName?: string | null;
  periodStartUtc: string;
  periodEndUtc: string;
  status: string;
  grossTokenAmount: number;
  sharePercent: number;
  shareTokenAmount: number;
  exchangeRate: number;
  currencyAmount: number;
  currencyCode?: string | null;
  paymentReference?: string | null;
  paymentNote?: string | null;
  createdAtUtc: string;
  approvedAtUtc?: string | null;
  paidAtUtc?: string | null;
  sourceBreakdown: FinancePayoutSourceBreakdown[];
  entries: FinancePayoutStatementEntry[];
}

export async function getFinanceLedger(params?: {
  kind?: FinanceLedgerKind;
  start?: string;
  end?: string;
  skip?: number;
  take?: number;
}): Promise<FinanceLedgerResponse> {
  try {
    const res = await axios.get("/admin/finance/ledger", { params });
    const data = res.data ?? {};
    return {
      totalCount: Number(data.totalCount ?? 0),
      items: Array.isArray(data.items) ? (data.items as FinanceLedgerItem[]) : [],
    };
  } catch (error) {
    return handleApiError(error, "getFinanceLedger", { totalCount: 0, items: [] });
  }
}

export async function getFinanceLedgerDetail(ledgerId: string): Promise<any | null> {
  try {
    const res = await axios.get(`/admin/finance/ledger/${encodeURIComponent(ledgerId)}`);
    return res.data ?? null;
  } catch (error) {
    return handleApiError(error, "getFinanceLedgerDetail", null);
  }
}

export async function getFinanceReconciliation(take = 100): Promise<FinanceReconciliationResponse> {
  try {
    const res = await axios.get("/admin/finance/reconciliation", { params: { take } });
    const data = res.data ?? {};
    return {
      totalIssues: Number(data.totalIssues ?? 0),
      highSeverity: Number(data.highSeverity ?? 0),
      mediumSeverity: Number(data.mediumSeverity ?? 0),
      lowSeverity: Number(data.lowSeverity ?? 0),
      items: Array.isArray(data.items) ? (data.items as FinanceReconciliationIssue[]) : [],
    };
  } catch (error) {
    return handleApiError(error, "getFinanceReconciliation", {
      totalIssues: 0,
      highSeverity: 0,
      mediumSeverity: 0,
      lowSeverity: 0,
      items: [],
    });
  }
}

export async function getFinancePayoutStatement(payoutId: string): Promise<FinancePayoutStatement | null> {
  try {
    const res = await axios.get(`/admin/finance/payouts/${encodeURIComponent(payoutId)}/statement`);
    return (res.data ?? null) as FinancePayoutStatement | null;
  } catch (error) {
    return handleApiError(error, "getFinancePayoutStatement", null);
  }
}

export async function approveFinancePayout(
  payoutId: string,
  payload: { reason: string; note?: string },
): Promise<boolean> {
  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/approve`, payload);
    return true;
  } catch (error) {
    return handleApiError(error, "approveFinancePayout", false);
  }
}

export async function rejectFinancePayout(
  payoutId: string,
  payload: { reason: string; note?: string },
): Promise<boolean> {
  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/reject`, payload);
    return true;
  } catch (error) {
    return handleApiError(error, "rejectFinancePayout", false);
  }
}

export async function markFinancePayoutPaid(
  payoutId: string,
  payload: { reason: string; paymentReference?: string; paymentNote?: string },
): Promise<boolean> {
  try {
    await axios.post(`/admin/finance/payouts/${payoutId}/mark-paid`, payload);
    return true;
  } catch (error) {
    return handleApiError(error, "markFinancePayoutPaid", false);
  }
}
